import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

// VSNP-704: warning toast surfaced by useIdleTimeout 60s before session
// expiry. Listens for the 'idle-warning' / 'idle-warning-clear' custom
// events so it stays decoupled from the hook itself. Any user activity
// (incl. clicking the "Stay signed in" button — which is just a normal
// click event that bubbles to document) resets the parent timer.

const IdleWarningToast = () => {
  const [visible, setVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const onShow = (e: Event) => {
      const detail = (e as CustomEvent<{ secondsRemaining: number }>).detail;
      setSecondsRemaining(detail?.secondsRemaining ?? 60);
      setVisible(true);
    };
    const onClear = () => setVisible(false);

    window.addEventListener('idle-warning', onShow);
    window.addEventListener('idle-warning-clear', onClear);
    return () => {
      window.removeEventListener('idle-warning', onShow);
      window.removeEventListener('idle-warning-clear', onClear);
    };
  }, []);

  useEffect(() => {
    if (!visible) {
      if (tickRef.current) clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      setSecondsRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg dark:border-amber-700 dark:bg-amber-900/40"
    >
      <Clock className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-300" aria-hidden="true" />
      <div className="max-w-xs text-sm text-amber-900 dark:text-amber-100">
        <p className="font-semibold">You're about to be signed out</p>
        <p className="mt-1">
          For your security, this session will end in {secondsRemaining} second{secondsRemaining === 1 ? '' : 's'} due to inactivity.
        </p>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="mt-2 rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700"
        >
          Stay signed in
        </button>
      </div>
    </div>
  );
};

export default IdleWarningToast;
